import { createReducer } from '@reduxjs/toolkit';

import { isTokenExpired } from '../../utils/helpers/tokenUtils';
import LS from '../../utils/localStorageUtils';
import { RootState } from '..';
import { UserProfile, UserStatistics } from '../types';
import {
	appleLoginFailure,
	appleLoginRequest,
	AuthFailurePayload,
	BanNotice,
	checkAuthFailure,
	checkAuthRequest,
	checkAuthSuccess,
	clearBanNotice,
	clearLoginPendingConfirmation,
	facebookLoginFailure,
	facebookLoginRequest,
	googleLoginFailure,
	googleLoginRequest,
	loginFailure,
	loginRequest,
	loginSuccess,
	logout,
	PendingLoginConfirmation,
	profileFailure,
	profileRequest,
	profileSuccess,
	registerFailure,
	registerRequest,
	registerSuccess,
	rehydrateAuth,
	reportUserFailure,
	reportUserRequest,
	reportUserSuccess,
	setBanNotice,
	setLoginPendingConfirmation,
	updateBonusCooldown,
	updateProfileFailure,
	updateProfileRequest,
	updateProfileSuccess,
	updateUserChips,
	updateUserLevel,
	updateUserVipStatus,
	userStatsFailure,
	userStatsRequest,
	userStatsSuccess,
} from './authActions';

export type AuthState = {
	token?: string;
	user?: UserProfile;
	loading?: boolean;
	profileLoading?: boolean;
	error?: AuthFailurePayload;
	profileError?: string;
	isLogged?: boolean;
	statistics?: UserStatistics;
	statisticsLoading?: boolean;
	statisticsError?: string;
	reportLoading?: boolean;
	reportError?: string;
	reportSuccess?: boolean;
	pendingConfirmation?: PendingLoginConfirmation | null;
	banNotice?: BanNotice | null;
};

export const initialState: AuthState = {
	loading: false,
	isLogged: false,
	profileLoading: false,
	token: undefined,
	user: undefined,
	error: undefined,
	profileError: undefined,
	statistics: undefined,
	statisticsLoading: undefined,
	statisticsError: undefined,
	reportLoading: false,
	reportError: undefined,
	reportSuccess: false,
	pendingConfirmation: null,
	banNotice: null,
};

export const authReducer = createReducer(initialState, builder => {
	builder
		.addCase(loginRequest, state => {
			state.loading = true;
			state.error = undefined;
			state.pendingConfirmation = null;
		})
		.addCase(appleLoginRequest, state => {
			state.loading = true;
			state.error = undefined;
			state.pendingConfirmation = null;
		})
		.addCase(facebookLoginRequest, state => {
			state.loading = true;
			state.error = undefined;
			state.pendingConfirmation = null;
		})
		.addCase(googleLoginRequest, state => {
			state.loading = true;
			state.error = undefined;
			state.pendingConfirmation = null;
		})
		.addCase(loginSuccess, (state, action) => {
			console.log('[AuthReducer] loginSuccess', action.payload);
			state.loading = false;
			state.token = action.payload.token;
			state.user = action.payload.user;
			// Set isLogged based on token validity (matching checkAuthSuccess behavior)
			const tokenValid = Boolean(action.payload.token) && !isTokenExpired(action.payload.token);
			state.isLogged = tokenValid;
			console.log('[AuthReducer] Token set:', !!action.payload.token);
			console.log('[AuthReducer] Token expired check:', isTokenExpired(action.payload.token));
			console.log('[AuthReducer] isLogged set to:', tokenValid);
		})
		.addCase(loginFailure, (state, action) => {
			state.loading = false;
			state.error = action.payload;
			state.isLogged = false;
		})
		.addCase(appleLoginFailure, (state, action) => {
			state.loading = false;
			state.error = { message: action.payload };
			state.isLogged = false;
		})
		.addCase(facebookLoginFailure, (state, action) => {
			state.loading = false;
			state.error = { message: action.payload };
			state.isLogged = false;
		})
		.addCase(googleLoginFailure, (state, action) => {
			state.loading = false;
			state.error = { message: action.payload };
			state.isLogged = false;
		})
		.addCase(logout, state => {
			state.token = undefined;
			state.user = undefined;
			state.isLogged = false;
			state.pendingConfirmation = null;
		})
		.addCase(registerRequest, state => {
			state.loading = true;
			state.error = undefined;
		})
		.addCase(registerSuccess, state => {
			state.loading = false;
		})
		.addCase(checkAuthRequest, state => {
			state.loading = true;
		})
		.addCase(checkAuthSuccess, (state, action) => {
			console.log('checkAuthSuccess', action.payload);
			state.loading = false;
			state.token = action.payload.token;
			// state.user = action.payload.user;
			// Set isLogged based on token validity
			const tokenValid = Boolean(action.payload.token) && !isTokenExpired(action.payload.token);
			state.isLogged = tokenValid;
			console.log('[AuthReducer] checkAuthSuccess - isLogged set to:', tokenValid);
		})
		.addCase(checkAuthFailure, state => {
			state.loading = false;
			state.token = undefined;
			state.user = undefined;
			state.isLogged = false;
		})
		.addCase(profileRequest, state => {
			state.profileLoading = true;
			state.profileError = undefined;
		})
		.addCase(profileSuccess, (state, action) => {
			console.log('[AuthReducer] ✅ profileSuccess reducer hit', action.payload?.username);
			state.profileLoading = false;
			state.user = action.payload;
		})
		.addCase(profileFailure, (state, action) => {
			state.profileLoading = false;
			state.profileError = action.payload;
		})
		.addCase(updateBonusCooldown, (state, action) => {
			if (!state.user) return;

			state.user = {
				...state.user,
				collectedAt: action.payload.collectedAt,
				bonusHours: action.payload.bonusHours ?? state.user.bonusHours,
			};
		})
		.addCase(updateUserChips, (state, action) => {
			if (!state.user) return;

			state.user = {
				...state.user,
				chips: state.user.chips + action.payload,
			};
			LS.set('auth_user', JSON.stringify(state.user));
		})
		.addCase(updateProfileRequest, state => {
			state.profileLoading = true;
			state.profileError = undefined;
		})
		.addCase(updateProfileSuccess, state => {
			state.profileLoading = false;
			state.profileError = undefined;
		})
		.addCase(updateProfileFailure, (state, action) => {
			state.profileLoading = false;
			state.profileError = action.payload;
		})
		.addCase(reportUserRequest, state => {
			state.reportLoading = true;
			state.reportError = undefined;
			state.reportSuccess = false;
		})
		.addCase(reportUserSuccess, state => {
			state.reportLoading = false;
			state.reportError = undefined;
			state.reportSuccess = true;
		})
		.addCase(reportUserFailure, (state, action) => {
			state.reportLoading = false;
			state.reportError = action.payload;
			state.reportSuccess = false;
		})
		.addCase(registerFailure, (state, action) => {
			state.loading = false;
			state.error = action.payload;
		})
		.addCase(userStatsRequest, state => {
			state.statisticsLoading = true;
			state.statisticsError = undefined;
		})
		.addCase(userStatsSuccess, (state, action) => {
			state.statisticsLoading = false;
			state.statistics = action.payload;
		})
		.addCase(userStatsFailure, (state, action) => {
			state.statisticsLoading = false;
			state.statisticsError = action.payload;
		})
		.addCase(rehydrateAuth, (state, action) => {
			if (action.payload.token) {
				state.token = action.payload.token;
			}
			// Only fills the gap before the profile request resolves - a profile already in
			// state is always fresher than the cached copy and must not be overwritten.
			if (!state.user && action.payload.user) {
				state.user = action.payload.user;
			}
		})
		.addCase(setLoginPendingConfirmation, (state, action) => {
			state.loading = false;
			state.pendingConfirmation = action.payload;
		})
		.addCase(setBanNotice, (state, action) => {
			state.banNotice = action.payload;
			state.pendingConfirmation = null;
		})
		.addCase(clearBanNotice, state => {
			state.banNotice = null;
		})
		.addCase(clearLoginPendingConfirmation, state => {
			state.pendingConfirmation = null;
		})
		.addCase(updateUserVipStatus, (state, action) => {
			if (state.user) {
				state.user = {
					...state.user,
					isVip: action.payload.isVip,
				};

				try {
					LS.set('auth_user', JSON.stringify(state.user));
				} catch (e) {
					console.error('Failed to save updated user VIP status to localStorage', e);
				}
			}
		})
		.addCase(updateUserLevel, (state, action) => {
			if (state.user) {
				state.user = {
					...state.user,
					level: action.payload,
				};

				try {
					LS.set('auth_user', JSON.stringify(state.user));
				} catch (e) {
					console.error('Failed to save updated user level to localStorage', e);
				}
			}
		});
});

export const selectIsLogged = (s: RootState) => {
	const token = s.auth.token;
	const hasToken = Boolean(token);

	// If there's a token, check if it's expired
	// But be lenient - if token exists and state says we're logged in, trust it
	// This handles the case where token validation might fail due to timing issues
	if (!hasToken) {
		return false;
	}

	// Check if token is expired
	const notExpired = !isTokenExpired(token);

	// Also check the state.isLogged flag as a fallback
	// This ensures that if loginSuccess set isLogged=true, we trust it
	const stateSaysLogged = s.auth.isLogged === true;

	// User is logged in if: token exists AND (not expired OR state says logged in)
	// This handles race conditions where token validation might fail temporarily
	const result = hasToken && (notExpired || stateSaysLogged);

	return result;
};
export const selectProfile = (s: RootState) => s.auth.user;
export const selectProfileLoading = (s: RootState) => s.auth.profileLoading;
export const selectProfileError = (s: RootState) => s.auth.profileError;
export const selectReportLoading = (s: RootState) => s.auth.reportLoading;
export const selectBanNotice = (s: RootState) => s.auth.banNotice;
export const selectReportError = (s: RootState) => s.auth.reportError;
export const selectReportSuccess = (s: RootState) => s.auth.reportSuccess;
export const selectStats = (s: RootState) => s.auth.statistics;
export const selectStatsLoading = (s: RootState) => s.auth.statisticsLoading;
